import { useMemo, useCallback } from "react";
import { Fuel, FuelUnit } from "@/types/fuels/fuels.types";
import { normalizeText } from "@/lib/utils";

interface UseFuelFiltersProps {
  fuels: Fuel[];
  searchTerm: string;
  showArchived: boolean;
  nameFilters: string[];
  unitFilters: FuelUnit[];
}

export const useFuelFilters = ({
  fuels,
  searchTerm,
  showArchived,
  nameFilters,
  unitFilters,
}: UseFuelFiltersProps) => {
  const filteredFuels = useMemo(() => {
    return fuels.filter((fuel) => {
      // Filter by archive status
      if (fuel.isArchived && !showArchived) {
        return false;
      }
      // Filter by name
      if (nameFilters.length > 0 && !nameFilters.includes(fuel.name)) {
        return false;
      }
      // Filter by unit (array)
      if (unitFilters.length > 0 && !fuel.units.some((unit) => unitFilters.includes(unit))) {
        return false;
      }
      // Filter by search term (search in name and units)
      if (searchTerm) {
        const normalizedSearch = normalizeText(searchTerm);
        const normalizedName = normalizeText(fuel.name);
        const normalizedUnits = fuel.units.map(normalizeText).join(" ");
        return (
          normalizedName.includes(normalizedSearch) ||
          normalizedUnits.includes(normalizedSearch)
        );
      }
      return true;
    });
  }, [fuels, searchTerm, showArchived, nameFilters, unitFilters]);

  const getUniqueOptions = useCallback(
    (field: "name" | "unit") => {
      if (field === "name") {
        const options = filteredFuels
          .map((fuel) => fuel.name)
          .filter((value, index, self) => self.indexOf(value) === index)
          .sort()
          .map((value) => ({ label: value, value }));
        return options;
      } else if (field === "unit") {
        // Collect all unique units from all fuels
        const allUnits = filteredFuels.flatMap((fuel) => fuel.units);
        const uniqueUnits = Array.from(new Set(allUnits));
        return uniqueUnits.sort().map((value) => ({ label: value, value }));
      }
      return [];
    },
    [filteredFuels]
  );

  return {
    filteredFuels,
    getUniqueOptions,
  };
}; 